// Batch Actions Bar for multi-file operations
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, FolderInput, Tag as TagIcon, ListChecks, Download, Link as LinkIcon, Package } from 'lucide-react';
import { FileStatus, Tag } from '@types';

interface BatchActionsProps {
  selectedCount: number;
  type?: 'files' | 'folders';
  onClear: () => void;
  onDelete: () => void;
  onMove: () => void;
  onAddTags: (tags: Tag[]) => void;
  onChangeStatus: (status: FileStatus) => void;
  onDownload: () => void;
  onLink: () => void;
  onCreateBeatPack?: () => void;
}

export const BatchActions: React.FC<BatchActionsProps> = ({
  selectedCount,
  type = 'files',
  onClear,
  onDelete,
  onMove,
  onAddTags,
  onChangeStatus,
  onDownload,
  onLink,
  onCreateBeatPack,
}) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(false);

  const statusOptions = [
    { value: FileStatus.WIP, label: 'WIP', emoji: '🚧' },
    { value: FileStatus.DRAFT, label: 'Draft', emoji: '🟡' },
    { value: FileStatus.TAGGED, label: 'Tagged', emoji: '🏷️' },
    { value: FileStatus.UNTAGGED, label: 'Clean', emoji: '✨' },
    { value: FileStatus.READY, label: 'Ready', emoji: '🟢' },
  ];

  const quickTags: Tag[] = [
    { id: '1', label: 'Hip Hop', type: 'genre' as any },
    { id: '2', label: 'Trap', type: 'genre' as any },
    { id: '5', label: 'Dark', type: 'mood' as any },
    { id: '6', label: 'Energetic', type: 'mood' as any },
  ];

  return (
    <>
      <div className="fixed bottom-4 md:bottom-6 left-4 md:left-1/2 md:-translate-x-1/2 right-4 md:right-auto z-[100] animate-slide-in-up max-w-full md:max-w-max">
        <div className="bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl shadow-2xl px-4 md:px-6 py-4 md:py-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            {/* Selection Info - Mobile First */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 md:p-2 bg-primary/20 rounded-lg">
                <TagIcon className="w-5 h-5 md:w-5 md:h-5 text-primary" />
              </div>
              <div>
                <p className="font-codec font-bold text-white text-sm md:text-sm">
                  {selectedCount} {type === 'folders' ? 'folder' : 'file'}{selectedCount !== 1 ? 's' : ''} selected
                </p>
                <button
                  onClick={onClear}
                  className="text-xs md:text-xs text-white/60 hover:text-white transition-colors touch-manipulation min-h-[32px]"
                >
                  Clear selection
                </button>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden md:block h-10 w-px bg-white/10" />

            {/* Actions - Mobile First with scrollable horizontal layout */}
            <div className="flex flex-wrap items-center gap-2 md:gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {/* Link Files - Mobile First - Only for files */}
              {type === 'files' && (
                <button
                  onClick={onLink}
                  className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                  title="Link files together"
                  aria-label="Link files"
                >
                  <LinkIcon className="w-5 h-5 md:w-4 md:h-4 text-white" />
                  <span className="hidden md:inline font-inter text-sm text-white font-semibold">Link</span>
                </button>
              )}

              {/* Change Status - Mobile First - Only for files */}
              {type === 'files' && (
                <div className="relative">
                  <button
                    onClick={() => setShowStatusMenu(!showStatusMenu)}
                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                    aria-label="Change status"
                  >
                    <ListChecks className="w-5 h-5 md:w-4 md:h-4 text-white" />
                    <span className="hidden md:inline font-inter text-sm text-white font-semibold">Status</span>
                  </button>

                  {showStatusMenu && createPortal(
                    <>
                      <div
                        className="fixed inset-0 z-[200]"
                        onClick={() => setShowStatusMenu(false)}
                      />
                      <div className="fixed bottom-24 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-auto z-[201] bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl p-2 min-w-[200px] max-w-[calc(100vw-2rem)]">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              onChangeStatus(option.value);
                              setShowStatusMenu(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors text-left touch-manipulation"
                          >
                            <span>{option.emoji}</span>
                            <span className="font-inter text-sm">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              )}

              {/* Add Tags - Mobile First - Only for files */}
              {type === 'files' && (
                <div className="relative">
                  <button
                    onClick={() => setShowTagMenu(!showTagMenu)}
                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                    aria-label="Add tags"
                  >
                    <TagIcon className="w-5 h-5 md:w-4 md:h-4 text-white" />
                    <span className="hidden md:inline font-inter text-sm text-white font-semibold">Tags</span>
                  </button>

                  {showTagMenu && createPortal(
                    <>
                      <div
                        className="fixed inset-0 z-[200]"
                        onClick={() => setShowTagMenu(false)}
                      />
                      <div className="fixed bottom-24 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-auto z-[201] bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl p-3 min-w-[250px] max-w-[calc(100vw-2rem)]">
                        <p className="text-xs text-white/60 font-inter font-bold uppercase mb-2">Quick Tags</p>
                        <div className="flex flex-wrap gap-2">
                          {quickTags.map((tag) => (
                            <button
                              key={tag.id}
                              onClick={() => {
                                onAddTags([tag]);
                                setShowTagMenu(false);
                              }}
                              className="px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-full text-xs font-inter font-semibold transition-all touch-manipulation"
                            >
                              {tag.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              )}

              {/* Move - Mobile First */}
              <button
                onClick={onMove}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                title="Move to folder"
                aria-label="Move files"
              >
                <FolderInput className="w-5 h-5 md:w-4 md:h-4 text-white" />
                <span className="hidden md:inline font-inter text-sm text-white font-semibold">Move</span>
              </button>

              {/* Add to Beat Pack - Mobile First - Only for files */}
              {type === 'files' && onCreateBeatPack && (
                <button
                  onClick={onCreateBeatPack}
                  className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                  title="Add to beat pack"
                  aria-label="Add to beat pack"
                >
                  <Package className="w-5 h-5 md:w-4 md:h-4" />
                  <span className="hidden md:inline font-inter text-sm font-semibold">Add to pack</span>
                </button>
              )}

              {/* Download - Mobile First */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🖱️ Download button clicked!');
                  onDownload();
                }}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all cursor-pointer touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                title="Download selected"
                aria-label="Download files"
              >
                <Download className="w-5 h-5 md:w-4 md:h-4 text-white" />
                <span className="hidden md:inline font-inter text-sm text-white font-semibold">Download</span>
              </button>

              {/* Delete - Mobile First */}
              <button
                onClick={onDelete}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-2 bg-danger/20 hover:bg-danger/30 text-danger rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0"
                title="Delete selected"
                aria-label="Delete files"
              >
                <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                <span className="hidden md:inline font-inter text-sm font-semibold">Delete</span>
              </button>
            </div>

            {/* Close Button - Mobile First */}
            <button
              onClick={onClear}
              className="p-2.5 md:p-2 hover:bg-white/10 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] md:min-h-0 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-5 h-5 md:w-5 md:h-5 text-white/60 hover:text-white" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

